import { onCleanup, onMount } from "solid-js";
import { metrics } from "../../stores/metrics.ts";

/**
 * Full-viewport WebGL canvas that runs the ambient health shader.
 * Color tint reflects overall system health: greenish when nominal,
 * amber when degraded, red when critical.
 *
 * Phase 1: load the GLSL fragment from /shaders/ambient-health.frag.
 */
export function AmbientCanvas() {
  let canvas!: HTMLCanvasElement;
  let raf: number | null = null;

  onMount(() => {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const tick = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = (canvas.width = window.innerWidth * dpr);
      const h = (canvas.height = window.innerHeight * dpr);

      // Cheap CSS-style gradient placeholder; Phase 1 swaps to WebGL shader.
      const grad = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) / 1.2);
      const tint = overallTint();
      grad.addColorStop(0, tint);
      grad.addColorStop(1, "#0a0a0f");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
  });

  onCleanup(() => {
    if (raf) cancelAnimationFrame(raf);
  });

  function overallTint() {
    const statuses = Object.values(metrics.health).map((h) => h?.status);
    if (statuses.includes("unhealthy")) return "rgba(255, 51, 51, 0.08)";
    if (statuses.includes("degraded")) return "rgba(240, 180, 41, 0.06)";
    return "rgba(0, 255, 136, 0.04)";
  }

  return (
    <canvas
      ref={canvas}
      class="pointer-events-none fixed inset-0 -z-10 h-screen w-screen"
    />
  );
}
